import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import {
  ORGANIZATION_SALES_ROLE_TYPES,
  RoleTypesEnum,
} from 'dash-common';
import { Form, FormContext } from 'dash-ui';
import {
  GetPropertiesFilterInput,
  useGetCurrentDeveloperPropertiesQuery,
} from 'gql/generated.graphql';

import { FormFields } from 'pages/OrganizationPage/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/components/SaveOrganizationUsersModal/constants/constants';
import useValidation from 'pages/OrganizationPage/OrganizationDetailsPage/components/OrganizationDetailsTabs/components/OrganizationUsers/components/SaveOrganizationUsersModal/hooks/useValidation';

const toSelectItems = (item: { _id: string; name: string }) => ({
  value: item._id,
  label: item.name,
});

const PropertiesSelect = ({ organizationId }: { organizationId: string }) => {
  const [queryFilter, setQueryFilter] =
    useState<GetPropertiesFilterInput | null>(null);
  const { t } = useTranslation(['forms', 'common', 'pages']);
  const { t: errorSlugs, i18n: i18nErrorSlugs } = useTranslation('common', {
    keyPrefix: 'serverMessages',
  });
  const { formErrors, formState, stateHandlers, formOnError } =
    useContext(FormContext);
  const valid = useValidation();
  const queryVariables = useMemo(
    () => ({
      filter: queryFilter,
      pagination: { skip: 0, limit: 10 },
      sort: [{ fieldName: 'createdAt', sortOrder: -1 }],
    }),
    [queryFilter]
  );
  const allowedPropertySelectForRoles = [
    ...ORGANIZATION_SALES_ROLE_TYPES,
    RoleTypesEnum.ORG_DEVELOPER,
  ];
  const {
    data,
    refetch,
    loading: propertiesLoading,
  } = useGetCurrentDeveloperPropertiesQuery({
    variables: queryVariables,
    skip: !queryFilter,
    fetchPolicy: 'network-only',
  });

  const properties = data?.getCurrentDeveloperProperties.collection || [];

  const onError = (error: { message: string }) => {
    const errorS = i18nErrorSlugs.exists(error.message)
      ? errorSlugs(error.message)
      : errorSlugs`unknown`;
    formOnError(errorS || '');
  };

  // FIXME: re-check callback
  const handleSelectInputs = useCallback(
    (value: string, values: string[] | undefined) => {
      const selectedProperties =
        properties.filter(({ _id }) => values?.includes(_id)) || [];
      stateHandlers[FormFields.Properties](selectedProperties);
      valid(FormFields.Properties, selectedProperties);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [properties]
  );

  const loadOptionsDebounced = useDebouncedCallback(
    (
      inputValue: string,
      callback: (options: Array<{ label: string; value: string }>) => void
    ) => {
      refetch({
        ...queryVariables,
        filter: {
          ...queryFilter,
          ...(inputValue.length > 3 ? { searchByName: inputValue } : {}),
        },
      })
        .then(({ data }) => {
          const items = (
            data?.getCurrentDeveloperProperties?.collection || []
          ).map(toSelectItems);
          callback(items);
        })
        .catch(onError);
    },
    500
  );

  // FIXME: re-creating context functions everytime state change. Use useCallback
  useEffect(() => {
    setQueryFilter(
      allowedPropertySelectForRoles.includes(formState.role?.type)
        ? { organizationId }
        : null
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState.role, formState.organizationSaleTeam]);

  return allowedPropertySelectForRoles.includes(formState?.role?.type) ? (
    <Form.Group w="100%" mt={3}>
      <Form.Label>{t('forms:labels:properties')}</Form.Label>
      <Form.Select
        menuPosition="fixed"
        isAsync
        isMulti
        isSelectAll
        isLoading={propertiesLoading}
        defaultOptions={properties.map(toSelectItems)}
        loadOptions={loadOptionsDebounced}
        placeholder={t('forms:placeholders.properties')}
        w="100%"
        onChanged={handleSelectInputs}
        $errorType={formErrors[FormFields.Properties]?.type}
      />
      <Form.Feedback $errorType={formErrors[FormFields.Properties]?.type}>
        {formErrors[FormFields.Properties]?.message}
      </Form.Feedback>
    </Form.Group>
  ) : null;
};

export default PropertiesSelect;
